import { getTenantClient } from '@sazeo/supabase-tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from '../people';

export type CompanySize = '1_10' | '11_50' | '51_200' | '201_500' | '501_1000' | '1000_plus';
export type RevenueRange = 'under_1m' | '1m_10m' | '10m_50m' | '50m_plus';

export interface CompanyProfile {
  id: string;
  tenant_id: string;
  name: string;
  website: string | null;
  industry: string | null;
  company_size: CompanySize | null;
  founding_year: number | null;
  headquarters: string | null;
  description: string | null;
  brand_color: string;
  logo_storage_path: string | null;
  revenue_range: RevenueRange | null;
  social_links: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export type UpsertCompanyProfileInput = Omit<
  CompanyProfile,
  'id' | 'tenant_id' | 'created_at' | 'updated_at'
>;

function db(client?: SupabaseClientLike | null): SupabaseClientLike {
  return client ?? (getTenantClient() as unknown as SupabaseClientLike);
}

async function currentTenantId(client: SupabaseClientLike): Promise<string> {
  if (!client.rpc) throw new Error('Supabase RPC client is required');
  const { data, error } = await client.rpc('current_tenant_id');
  if (error) throw new Error(error.message ?? 'Unable to resolve tenant');
  if (!data) throw new Error('Active tenant is required');
  return String(data);
}

export const companyProfileKeys = {
  all: ['hr-company-profile'] as const,
  current: () => [...companyProfileKeys.all, 'current'] as const,
};

export function useCompanyProfile(client?: SupabaseClientLike | null) {
  return useQuery({
    queryKey: companyProfileKeys.current(),
    queryFn: async (): Promise<CompanyProfile | null> => {
      const { data, error } = await db(client)
        .from('hr_company_profiles')
        .select('*')
        .maybeSingle();
      if (error) throw new Error(error.message ?? 'Unable to load company profile');
      return (data as CompanyProfile | null) ?? null;
    },
  });
}

export function useUpsertCompanyProfile(client?: SupabaseClientLike | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertCompanyProfileInput): Promise<CompanyProfile> => {
      const supabase = db(client);
      const tenantId = await currentTenantId(supabase);
      const { data, error } = await supabase
        .from('hr_company_profiles')
        .upsert(
          {
            ...input,
            tenant_id: tenantId,
          },
          { onConflict: 'tenant_id' },
        )
        .select('*')
        .single();
      if (error) throw new Error(error.message ?? 'Unable to save company profile');
      return data as CompanyProfile;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyProfileKeys.all });
    },
  });
}
